import {
  createSigningRequestSchema,
  oidcAuthorizationSchema,
  oidcProvidersSchema,
  problemSchema,
  sessionSchema,
  signingCancellationResultSchema,
  signingProgressEventSchema,
  signingRequestSchema,
  studioOverviewSchema,
  userSchema,
  type CreateSigningRequestInput,
  type Problem,
  type OidcProvider,
  type Session,
  type SigningRequest,
  type SigningCancellationResult,
  type SigningProgressEvent,
  type StudioOverview,
  type User
} from "@vericreator/contracts";

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "/v1";

export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly status: number
  ) {
    super(problem.code);
  }
}

interface RequestOptions extends Omit<RequestInit, "body" | "headers"> {
  readonly accessToken?: string;
  readonly body?: unknown;
  readonly idempotencyKey?: string;
}

async function request<T>(path: string, schema: { parse(value: unknown): T }, options: RequestOptions = {}): Promise<T> {
  const { accessToken, body, idempotencyKey, ...requestInit } = options;
  const headers = new Headers();
  if (body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (accessToken) {
    headers.set("authorization", `Bearer ${accessToken}`);
  }
  if (idempotencyKey) {
    headers.set("idempotency-key", idempotencyKey);
  }

  const init: RequestInit = {
    ...requestInit,
    headers,
    credentials: "include"
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const response = await fetch(`${apiBase}${path}`, init);

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    const parsed = problemSchema.safeParse(payload);
    throw new ApiError(
      parsed.success
        ? parsed.data
        : {
            type: "https://vericreator.com/problems/unexpected_response",
            title: "Unexpected response",
            status: response.status,
            code: "unexpected_response",
            traceId: crypto.randomUUID()
          },
      response.status
    );
  }

  return schema.parse(await response.json());
}

async function requestNoContent(path: string, options: RequestOptions = {}): Promise<void> {
  const { accessToken, body, idempotencyKey, ...requestInit } = options;
  const headers = new Headers();
  if (body !== undefined) headers.set("content-type", "application/json");
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
  if (idempotencyKey) headers.set("idempotency-key", idempotencyKey);
  const response = await fetch(`${apiBase}${path}`, {
    ...requestInit,
    headers,
    credentials: "include",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {})
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    const parsed = problemSchema.safeParse(payload);
    throw new ApiError(
      parsed.success
        ? parsed.data
        : {
            type: "https://vericreator.com/problems/unexpected_response",
            title: "Unexpected response",
            status: response.status,
            code: "unexpected_response",
            traceId: crypto.randomUUID()
          },
      response.status
    );
  }
}

export const api = {
  login: (email: string, password: string): Promise<Session> =>
    request("/auth/sessions", sessionSchema, { method: "POST", body: { email, password } }),
  identityProviders: (): Promise<readonly OidcProvider[]> =>
    request("/auth/oidc/providers", oidcProvidersSchema, { method: "GET" }).then(({ providers }) => providers),
  beginIdentityAuthorization: (provider: OidcProvider): Promise<{ authorizationUrl: string }> =>
    request("/auth/oidc/authorizations", oidcAuthorizationSchema, { method: "POST", body: { provider } }),
  refresh: (): Promise<Session> => request("/auth/tokens/refresh", sessionSchema, { method: "POST" }),
  logout: (): Promise<void> =>
    fetch(`${apiBase}/auth/sessions/current`, { method: "DELETE", credentials: "include" }).then((response) => {
      if (!response.ok && response.status !== 204) {
        throw new Error("logout_failed");
      }
    }),
  me: (accessToken: string): Promise<User> => request("/me", userSchema, { accessToken }),
  studio: (accessToken: string): Promise<StudioOverview> =>
    request("/studio", studioOverviewSchema, { accessToken }),
  createSigningRequest: (accessToken: string, input: CreateSigningRequestInput): Promise<SigningRequest> => {
    const verifiedInput = createSigningRequestSchema.parse(input);
    return request("/signing-requests", signingRequestSchema, {
      method: "POST",
      accessToken,
      body: verifiedInput,
      idempotencyKey: crypto.randomUUID()
    });
  },
  retrySigningRequest: (accessToken: string, requestId: string): Promise<SigningRequest> =>
    request(`/signing-requests/${requestId}/retries`, signingRequestSchema, {
      method: "POST",
      accessToken,
      body: {},
      idempotencyKey: crypto.randomUUID()
    }),
  cancelSigningRequest: (accessToken: string, requestId: string): Promise<void> =>
    requestNoContent(`/signing-requests/${requestId}`, { method: "DELETE", accessToken }),
  cancelActiveSigningRequests: (accessToken: string): Promise<SigningCancellationResult> =>
    request("/signing-requests/cancellations", signingCancellationResultSchema, {
      method: "POST",
      accessToken,
      body: {},
      idempotencyKey: crypto.randomUUID()
    }),
  createSigningProgressSubscription: (accessToken: string, requestId: string): Promise<void> =>
    requestNoContent(`/signing-requests/${requestId}/progress-subscriptions`, {
      method: "POST",
      accessToken
    }),
  openSigningProgressSocket: (requestId: string): WebSocket => {
    const httpUrl = new URL(`${apiBase}/signing-requests/${requestId}/progress-stream`, window.location.origin);
    httpUrl.protocol = httpUrl.protocol === "https:" ? "wss:" : "ws:";
    return new WebSocket(httpUrl.toString());
  },
  parseSigningProgressEvent: (value: unknown): SigningProgressEvent => signingProgressEventSchema.parse(value)
};
